import { useSubTask } from "../../../hooks";
import { useEffect, useState } from "react";

import { useTask } from "../../../hooks/query";

// Types
type TaskStatus = "NOT-STARTED" | "IN-PROGRESS" | "IN-REVIEW" | "COMPLETED";

type DBTask = {
    Id: string;
    ProjectId: string;
    ParentTaskId: string | null;
    Name: string;
    Description: string;
    Status: TaskStatus;
    SortOrder: number;
    CreatedAt: string;
    UpdatedAt: string;
};

type TaskCard = {
    id: string;
    content: string;
    tags: string[];
    assignee: string;
    createdAt: string;
    lastUpdated: string;
    taskSummary: DBTask;
};

type Column = {
    id: string;
    title: string;
    tasks: TaskCard[];
};

export default function useKanbanBoard() {
    const { tasks } = useSubTask();
    const [columns, setColumns] = useState<Column[]>([]);
    const { useUpdate } = useTask();
    const updateTaskSvc = useUpdate();

    const handleTaskDbUpdate = async (args: DBTask) => {
        try {
            const params = { ...args };

            await updateTaskSvc.mutateAsync(params);
        } catch (e) {
            console.error(e);
        }
    };

    const transformTasksToColumns = (tasksFromDB: DBTask[]): Column[] => {
        const statusMap: Record<TaskStatus, { id: string; title: string }> = {
            "NOT-STARTED": { id: "NOT-STARTED", title: "To Do" },
            "IN-PROGRESS": { id: "IN-PROGRESS", title: "In Progress" },
            "IN-REVIEW": { id: "IN-REVIEW", title: "In Review" },
            COMPLETED: { id: "COMPLETED", title: "Done" },
        };

        const columnsMap: Record<string, Column> = {
            "NOT-STARTED": { id: "NOT-STARTED", title: "To Do", tasks: [] },
            "IN-PROGRESS": { id: "IN-PROGRESS", title: "In Progress", tasks: [] },
            "IN-REVIEW": { id: "IN-REVIEW", title: "In Review", tasks: [] },
            COMPLETED: { id: "COMPLETED", title: "Done", tasks: [] },
        };

        for (const task of tasksFromDB) {
            const statusKey = task.Status as TaskStatus;
            const columnInfo = statusMap[statusKey];

            if (columnInfo) {
                const taskCard: TaskCard = {
                    id: task.Id,
                    content: task.Name,
                    tags: task.Description ? [task.Description] : [],
                    assignee: "", // Plug in real assignee if available
                    createdAt: task.CreatedAt,
                    lastUpdated: task.UpdatedAt,
                    taskSummary: task,
                };

                columnsMap[columnInfo.id].tasks.push(taskCard);
            }
        }

        return Object.values(columnsMap);
    };

    useEffect(() => {
        const kanbanTasks = transformTasksToColumns(tasks || []);
        setColumns(kanbanTasks);
    }, [tasks]);

    return { columns, tasks, setColumns, handleTaskDbUpdate };
}
